import { createApi } from '../client';
import { toQuery } from '../query';
import type {
  InvoiceDocumentType,
  InvoiceStatus,
  InvoiceSummary,
  LicenceState,
  Paginated,
  SortDirection,
} from '../shared';

import type {
  AuditEntry,
  AuditSortKey,
  BillingSettings,
  CreateTenantInput,
  InvoiceDetail,
  InvoiceSortKey,
  LicenceInput,
  LicenceOrderInput,
  PaymentInput,
  PlatformAuthentication,
  PlatformIntegrationLink,
  PlatformUser,
  PriceList,
  Quote,
  SupplierProfile,
  TenantDetail,
  TenantSortKey,
  TenantSummary,
} from './types';

/** One function per admin-api endpoint (R0002 Notes/api-contract.md). */
const api = createApi('platform');

export const login = (email: string, password: string): Promise<PlatformAuthentication> =>
  api.post<PlatformAuthentication>('/auth/login', { email, password });

export const readMe = (): Promise<PlatformUser> => api.get<PlatformUser>('/auth/me');

export const logout = (): Promise<{ revoked: number }> => api.post<{ revoked: number }>('/auth/logout', {});

export const searchTenants = (search: {
  term: string;
  licenceState?: LicenceState;
  page: number;
  sort: TenantSortKey;
  direction: SortDirection;
}): Promise<Paginated<TenantSummary>> => api.get<Paginated<TenantSummary>>(`/tenants${toQuery(search)}`);

export const createTenant = (input: CreateTenantInput): Promise<TenantDetail> =>
  api.post<TenantDetail>('/tenants', input);

export const readTenant = (tenantId: string): Promise<TenantDetail> =>
  api.get<TenantDetail>(`/tenants/${encodeURIComponent(tenantId)}`);

export const updateTenant = (
  tenantId: string,
  changes: { name?: string; contactEmail?: string },
): Promise<TenantDetail> => api.patch<TenantDetail>(`/tenants/${encodeURIComponent(tenantId)}`, changes);

export const saveLicence = (tenantId: string, licence: LicenceInput): Promise<TenantDetail> =>
  api.put<TenantDetail>(`/tenants/${encodeURIComponent(tenantId)}/licence`, licence);

export const suspendLicence = (tenantId: string, reason: string): Promise<TenantDetail> =>
  api.post<TenantDetail>(`/tenants/${encodeURIComponent(tenantId)}/licence/suspend`, { reason });

export const reactivateLicence = (tenantId: string): Promise<TenantDetail> =>
  api.post<TenantDetail>(`/tenants/${encodeURIComponent(tenantId)}/licence/reactivate`, {});

export const resetOwnerPassword = (tenantId: string, password: string): Promise<{ revoked: number }> =>
  api.post<{ revoked: number }>(`/tenants/${encodeURIComponent(tenantId)}/owner/password`, { password });

export const resendOwnerInvitation = (tenantId: string): Promise<void> =>
  api.post<void>(`/tenants/${encodeURIComponent(tenantId)}/owner/invitation`, {});

/** Always accepted — the API never says whether the e-mail has an account. */
export const requestPasswordReset = (email: string): Promise<void> =>
  api.post<void>('/auth/password/forgot', { email });

export const setPasswordFromLink = (token: string, password: string): Promise<void> =>
  api.post<void>('/auth/password/set', { token, password });

export const searchAudit = (search: {
  tenantId?: string;
  action?: string;
  page: number;
  sort: AuditSortKey;
  direction: SortDirection;
}): Promise<Paginated<AuditEntry>> => api.get<Paginated<AuditEntry>>(`/audit${toQuery(search)}`);

export const readBillingSettings = (): Promise<BillingSettings> =>
  api.get<BillingSettings>('/settings/billing');

export const saveSupplierProfile = (profile: SupplierProfile): Promise<SupplierProfile> =>
  api.put<SupplierProfile>('/settings/billing/supplier', profile);

export const savePriceList = (priceList: PriceList): Promise<PriceList> =>
  api.put<PriceList>('/settings/billing/price-list', priceList);

export const quoteLicence = (order: LicenceOrderInput): Promise<Quote> =>
  api.post<Quote>('/settings/billing/quote', order);

export const orderLicence = (tenantId: string, order: LicenceOrderInput): Promise<InvoiceDetail> =>
  api.post<InvoiceDetail>(`/tenants/${encodeURIComponent(tenantId)}/licence/orders`, order);

export const searchInvoices = (search: {
  tenantId?: string;
  term: string;
  documentType?: InvoiceDocumentType;
  status?: InvoiceStatus;
  overdue?: boolean;
  page: number;
  sort: InvoiceSortKey;
  direction: SortDirection;
}): Promise<Paginated<InvoiceSummary>> =>
  api.get<Paginated<InvoiceSummary>>(
    `/invoices${toQuery({ ...search, overdue: search.overdue ? 'true' : undefined })}`,
  );

const invoice = (invoiceId: string): string => `/invoices/${encodeURIComponent(invoiceId)}`;

export const readInvoice = (invoiceId: string): Promise<InvoiceDetail> =>
  api.get<InvoiceDetail>(invoice(invoiceId));

export const downloadInvoicePdf = (invoiceId: string): Promise<Blob> => api.blob(`${invoice(invoiceId)}/pdf`);

export const recordPayment = (invoiceId: string, payment: PaymentInput): Promise<InvoiceDetail> =>
  api.post<InvoiceDetail>(`${invoice(invoiceId)}/payments`, payment);

export const deletePayment = (invoiceId: string, paymentId: string): Promise<InvoiceDetail> =>
  api.delete<InvoiceDetail>(`${invoice(invoiceId)}/payments/${encodeURIComponent(paymentId)}`);

export const cancelInvoice = (invoiceId: string): Promise<InvoiceDetail> =>
  api.post<InvoiceDetail>(`${invoice(invoiceId)}/cancel`, {});

export const listPlatformIntegrations = (): Promise<Array<PlatformIntegrationLink>> =>
  api.get<Array<PlatformIntegrationLink>>('/integrations');
